import { UploadOutlined } from "@ant-design/icons";
import { RecordIcon, UploadSimpleIcon } from "@phosphor-icons/react";
import { useTranslate } from "@refinedev/core";
import { Checkbox, Flex, Typography, notification } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import RecordRTC, { StereoAudioRecorder } from "recordrtc";
import { CustomFormItemProps } from "types";
import { AudioSample } from "./AudioSample";
import { BigInput } from "components/BigInput/BigInput/";
import { useAntTheme } from "hooks/useAntTheme";
import { StyledDragger } from "components/uploader/UploadFiles/";
import {
  useVoiceCloning,
  loadSound,
  MIN_TOTAL_DURATION,
  MAX_SAMPLE_DURATION,
  MAX_UPLOADED_SAMPLE_DURATION,
  RECORD_COUNTDOWN_DURATION,
} from "../hooks/useVoiceCloning";
import { useVoiceLanguages } from "../hooks/useVoiceLanguages";
import { VoiceCloneLanguageSelect } from "components/VoiceCloneLanguageSelect/";
import { VoiceCloneTextSample } from "./VoiceCloneTextSample";
import { Button, ButtonProps, radius } from "@libertify/ui";
import { VoiceRecording } from "./VoiceRecording";

export const VoiceProfileCreateForm: React.FC<
  CustomFormItemProps<string> & {
    defaultName?: string;
    showNameInput?: boolean;
    submitButtonProps?: ButtonProps;
    compact?: boolean;
  } & {
    onSuccess?: (voiceProfileId: string) => void;
    onCreatingChange?: (isCreating: boolean) => void;
    /** Called immediately when voice profile is created (before cloning completes) */
    onCreated?: (voiceProfileId: string) => void;
  }
> = ({
  onSuccess,
  defaultName,
  showNameInput = true,
  submitButtonProps,
  onCreatingChange,
  compact = false,
  onCreated,
}) => {
  const t = useTranslate();
  const { theme, isMobile } = useAntTheme();

  const [selectedLanguage, setSelectedLanguage] = useState<string | undefined>(
    undefined
  );

  const {
    isCreating,
    voiceName,
    setVoiceName,
    recordedSamples,
    uploadedSamples,
    consentChecked,
    setConsentChecked,
    createVoiceProfile,
    totalAudioSamplesDuration,
    canCreate,
    removeRecordedSample,
    removeUploadedSample,
    clearAllSamples,
    addRecordedSample,
    addUploadedSample,
  } = useVoiceCloning({
    defaultName,
    language: selectedLanguage,
    onCreated,
    onSuccess,
    onCreatingChange,
  });

  const { data: languagesList, isLoading: isLoadingLanguages } =
    useVoiceLanguages();

  const textForRecording = selectedLanguage
    ? (languagesList?.[selectedLanguage]?.text_for_recording ?? null)
    : null;

  const [recordCountdown, setRecordCountdown] = useState(
    RECORD_COUNTDOWN_DURATION
  );
  const [isCountingDown, setIsCountingDown] = useState(false);
  const [recording, setRecording] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<RecordRTC | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [uploadShown, setUploadShown] = useState(false);
  const recordingIntervalRef = useRef<NodeJS.Timeout>();
  const canUploadSamples = !isMobile;
  const isRecordingRef = useRef(false);

  const startRecord = async () => {
    const newStream = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });
    setRecording(true);
    // reset for next time
    setRecordCountdown(RECORD_COUNTDOWN_DURATION);
    setRecordingDuration(0); // Reset the timer to 0
    setStream(newStream);
    const newMediaRecorder = new RecordRTC(newStream, {
      recorderType: StereoAudioRecorder,
      mimeType: "audio/wav",
      // timeSlice: 250,
      bufferSize: 4096,
      numberOfAudioChannels: 1,
    });
    setMediaRecorder(newMediaRecorder);
    newMediaRecorder.startRecording();
  };
  useEffect(() => {
    if (!isCountingDown) return;

    const timeoutId = setTimeout(() => {
      if (recordCountdown <= 1) {
        setIsCountingDown(false);
        setRecordCountdown(RECORD_COUNTDOWN_DURATION);
        startRecord().catch((error) => {
          console.error("Error starting recording:", error);
        });
      } else {
        setRecordCountdown(recordCountdown - 1);
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [isCountingDown, recordCountdown]);

  const handleStartRecording = () => {
    if (isCountingDown || !selectedLanguage) return;
    setIsCountingDown(true);
    setRecordCountdown(RECORD_COUNTDOWN_DURATION);
  };
  const stopRecord = useCallback(() => {
    if (!isRecordingRef.current) return;
    isRecordingRef.current = false;
    setRecording(false);

    mediaRecorder?.stopRecording(() => {
      (async () => {
        const blob = mediaRecorder.getBlob();
        await addRecordedSample(blob);

        for (const track of stream?.getTracks() ?? []) {
          track.stop();
        }
        setRecordingDuration(0);
      })().catch((error) => {
        console.error("Error while stopping recording:", error);
      });
    });
  }, [mediaRecorder, stream, addRecordedSample]);

  useEffect(() => {
    if (recording) {
      recordingIntervalRef.current = setInterval(() => {
        setRecordingDuration((prevTime) => {
          if (prevTime + 1 >= MAX_SAMPLE_DURATION) {
            clearInterval(recordingIntervalRef.current);
            // Stop recording when max duration is reached
            // Using setTimeout to defer the stopRecord call outside the setState callback
            setTimeout(() => stopRecord(), 0);
          }
          return prevTime + 1;
        });
      }, 1000);
    }
    return () => clearInterval(recordingIntervalRef.current);
  }, [recording, stopRecord]);

  useEffect(() => {
    isRecordingRef.current = recording;
  }, [recording]);

  return (
    <Flex vertical gap={30}>
      {showNameInput && (
        <Flex vertical gap={5}>
          <Typography.Text>
            {t("media.components.VoiceProfileCreateForm.voiceName")}
          </Typography.Text>
          <BigInput
            value={voiceName}
            onChange={(e) => setVoiceName(e.target.value)}
            style={{ width: "100%", height: 40 }}
          />
        </Flex>
      )}

      <Flex vertical gap={10}>
        {!selectedLanguage && (
          <Typography.Text>
            {t("media.components.VoiceProfileCreateForm.selectLanguage")}
          </Typography.Text>
        )}
        <VoiceCloneLanguageSelect
          value={selectedLanguage}
          onChange={(value) => {
            if (!value) clearAllSamples();
            setSelectedLanguage(value);
          }}
          languages={languagesList}
          loading={isLoadingLanguages}
          hasSamples={recordedSamples.length > 0 || uploadedSamples.length > 0}
        />
      </Flex>
      {textForRecording && (
        <Flex vertical gap={8}>
          <Typography.Text
            style={{ textAlign: "center", whiteSpace: "pre-line" }}
          >
            {t("media.components.VoiceProfileCreateForm.toCreateA")}
          </Typography.Text>
          <Typography.Text
            type="danger"
            style={{ textAlign: "center", whiteSpace: "pre-line" }}
          >
            {t("media.components.VoiceProfileCreateForm.audioSamplesMinimum")}
          </Typography.Text>
        </Flex>
      )}

      {textForRecording && (
        <VoiceCloneTextSample textForRecording={textForRecording} />
      )}

      {recording && (
        <VoiceRecording
          stopRecord={stopRecord}
          recordingDuration={recordingDuration}
        />
      )}

      <Flex
        vertical
        style={
          compact
            ? { maxHeight: 150, overflowY: "auto", paddingRight: 8 }
            : undefined
        }
      >
        {totalAudioSamplesDuration > 0 && (
          <Typography.Title level={5} style={{ marginTop: 0 }}>
            {t("media.components.VoiceProfileCreateForm.audioSamples")}
          </Typography.Title>
        )}
        {recordedSamples.map((sample, index) => (
          <AudioSample
            key={index}
            audio={sample.audio}
            removeAudioSample={() => removeRecordedSample(index)}
          >
            <Typography.Text ellipsis>{`Sample #${index + 1}`}</Typography.Text>
          </AudioSample>
        ))}
        {uploadedSamples.map((sample, index) => (
          <AudioSample
            key={index}
            audio={sample.audio}
            removeAudioSample={() => removeUploadedSample(index)}
          >
            <Typography.Text ellipsis>
              {`Sample #${recordedSamples.length + index + 1}`}
            </Typography.Text>
          </AudioSample>
        ))}
        {totalAudioSamplesDuration > 0 && (
          <Flex style={{ marginTop: 10 }} justify="space-between">
            <Typography.Paragraph type="secondary">
              {t("media.components.VoiceProfileCreateForm.total")}
              {totalAudioSamplesDuration}
              {t("media.components.VoiceProfileCreateForm.seconds")}
            </Typography.Paragraph>
            <Typography.Paragraph type="secondary">
              {t("media.components.VoiceProfileCreateForm.minimum")}
              {MIN_TOTAL_DURATION}
              {t("media.components.VoiceProfileCreateForm.seconds")}
            </Typography.Paragraph>
          </Flex>
        )}
      </Flex>

      {!recording && recordCountdown !== 0 && selectedLanguage && (
        <Flex justify="space-between" gap={10}>
          <Button
            variant="primary"
            size={isMobile ? "l" : "m"}
            block={isMobile}
            shape="round"
            style={{ alignItems: "center", display: "flex" }}
            icon={
              <RecordIcon
                color={theme.colorBgBase}
                weight="fill"
                size={"1em"}
              />
            }
            onClick={handleStartRecording}
          >
            {isCountingDown
              ? `${t("media.components.VoiceProfileCreateForm.startingIn")} ${recordCountdown}`
              : t("media.components.VoiceProfileCreateForm.recordSample")}
          </Button>
          {!uploadShown && canUploadSamples && (
            <Button
              variant="outlined"
              size="m"
              shape="round"
              icon={<UploadSimpleIcon color={theme.colorPrimary} />}
              onClick={() => setUploadShown(true)}
            >
              {t("media.components.VoiceProfileCreateForm.uploadSamples")}
            </Button>
          )}
        </Flex>
      )}
      {uploadShown && (
        <Flex>
          <StyledDragger
            borderColor={theme.colorPrimary}
            maxCount={5}
            iconRender={(_file) => null}
            showUploadList={false}
            accept=".mp3, audio/mp3, audio/mpeg, audio/mpeg3, audio/x-mpeg-3, audio/mp4"
            customRequest={(args) => {
              (async ({ file }) => {
                const audio = await loadSound(file as Blob);

                if (audio.duration > MAX_UPLOADED_SAMPLE_DURATION) {
                  const sampleDuration = Math.floor(audio.duration);
                  notification.error({
                    message: t(
                      "media.components.VoiceProfileCreateForm.sampleTooLong"
                    ),
                    description: t(
                      "media.components.VoiceProfileCreateForm.sampleDuration",
                      {
                        duration: sampleDuration,
                        max: MAX_UPLOADED_SAMPLE_DURATION,
                      }
                    ),
                  });
                } else {
                  // valid file
                  await addUploadedSample(file as File);
                }
              })(args).catch((error) => {
                console.error("Error loading audio file:", error);
              });
            }}
          >
            <p>
              <UploadOutlined />
            </p>
            <p>{t("media.components.VoiceProfileCreateForm.clickOrDrag")}</p>
            <Typography.Text type="secondary">
              {t("media.components.VoiceProfileCreateForm.fileUploadWill")}
            </Typography.Text>
          </StyledDragger>
        </Flex>
      )}

      <Flex
        vertical
        gap={compact ? 15 : 20}
        style={{ marginTop: compact ? 15 : 30, marginBottom: compact ? 70 : 0 }}
        align={compact ? "stretch" : "center"}
      >
        <Checkbox
          checked={consentChecked}
          onChange={(e) => {
            setConsentChecked(e.target.checked);
            if (e.target.checked) setConsentError(false);
          }}
          style={{
            backgroundColor: "#f5f5f5",
            padding: compact ? "8px 12px" : "12px 16px",
            borderRadius: radius.box,
            width: "100%",
            maxWidth: compact ? "100%" : "600px",
            alignItems: "flex-start",
            border: `1px solid ${
              consentError ? theme.colorError : "transparent"
            }`,
          }}
        >
          <Typography.Text style={compact ? { fontSize: 12 } : undefined}>
            {t("media.components.VoiceProfileCreateForm.consentTextVoice")}
          </Typography.Text>
        </Checkbox>

        {canCreate && (
          <Button
            shape="round"
            size="l"
            variant="primary"
            onClick={() => {
              if (!consentChecked) {
                setConsentError(true);
                return;
              }
              createVoiceProfile().catch((error) => {
                console.error("Error creating voice profile:", error);
              });
            }}
            loading={isCreating}
            {...submitButtonProps}
          >
            {t("media.components.VoiceProfileCreateForm.cloneVoice")}
          </Button>
        )}
      </Flex>
    </Flex>
  );
};
